import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {   StudentAttendanceReport,   ClassReport,   ReportSummary } from './reportService';import type { AttendanceData } from './reportService';

export class ExportService {
  // Helper to format date for export
  private static formatDate(date: Date): string {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  }

  // Helper to format attendance rate
  private static formatRate(rate: number): string {
    return `${rate.toFixed(1)}%`;
  }

  // Helper to format date for column headers (YYYY-MM-DD)
  private static formatDateForColumn(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  // Export Attendance Report to Excel (Pivot format: Students as rows, Dates as columns)
  static exportAttendanceToExcel(data: AttendanceData[], fileName: string = 'attendance_report'): void {
    try {
      // Group data by student and date
      const studentMap = new Map<string, {
        studentId: string;
        studentName: string;
        rollNumber: string;
        attendanceByDate: Map<string, string>;
      }>();
      
      const allDates = new Set<string>();
      
      // Process attendance data
      data.forEach(record => {
        const studentKey = `${record.studentId}-${record.studentName}`;
        const dateKey = this.formatDateForColumn(record.date);
        
        allDates.add(dateKey);
        
        if (!studentMap.has(studentKey)) {
          studentMap.set(studentKey, {
            studentId: record.studentId,
            studentName: record.studentName,
            rollNumber: record.rollNumber,
            attendanceByDate: new Map()
          });
        }
        
        const student = studentMap.get(studentKey)!;
        student.attendanceByDate.set(dateKey, record.status);
      });
      
      // Sort dates
      const sortedDates = Array.from(allDates).sort();
      
      // Create header row
      const headers = ['Student ID', 'Student Name', 'Roll Number', ...sortedDates];
      
      // Create data rows
      const worksheetData: any[][] = [headers];
      
      studentMap.forEach(student => {
        const row = [
          student.studentId,
          student.studentName,
          student.rollNumber,
          ...sortedDates.map(date => student.attendanceByDate.get(date) || '')
        ];
        worksheetData.push(row);
      });
      
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      
      // Set column widths
      const colWidths = [
        { wch: 12 }, // Student ID
        { wch: 25 }, // Student Name
        { wch: 15 }, // Roll Number
        ...sortedDates.map(() => ({ wch: 12 })) // Date columns
      ];
      worksheet['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance Report');
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    } catch (error) {
      console.error('Error exporting attendance to Excel:', error);
      throw new Error('Failed to export attendance data to Excel');
    }
  }

  // Export Attendance Report to PDF (Pivot format)
  static exportAttendanceToPDF(data: AttendanceData[], fileName: string = 'attendance_report'): void {
    try {
      const doc = new jsPDF('landscape'); // Use landscape for better date column visibility
      
      // Add title
      doc.setFontSize(18);
      doc.text('Attendance Report', 14, 22);
      
      // Add generation date
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);
      doc.text(`Total Records: ${data.length}`, 14, 36);

      // Group data by student and date (same logic as Excel)
      const studentMap = new Map<string, {
        studentId: string;
        studentName: string;
        rollNumber: string;
        attendanceByDate: Map<string, string>;
      }>();
      
      const allDates = new Set<string>();
      
      // Process attendance data
      data.forEach(record => {
        const studentKey = `${record.studentId}-${record.studentName}`;
        const dateKey = this.formatDateForColumn(record.date);
        
        allDates.add(dateKey);
        
        if (!studentMap.has(studentKey)) {
          studentMap.set(studentKey, {
            studentId: record.studentId,
            studentName: record.studentName,
            rollNumber: record.rollNumber,
            attendanceByDate: new Map()
          });
        }
        
        const student = studentMap.get(studentKey)!;
        student.attendanceByDate.set(dateKey, record.status);
      });
      
      // Sort dates and limit for PDF (max 10 dates to fit on page)
      const sortedDates = Array.from(allDates).sort().slice(0, 10);
      
      // Create headers
      const headers = ['Student ID', 'Student Name', 'Roll Number', ...sortedDates];
      
      // Create table data
      const tableData: string[][] = [];
      studentMap.forEach(student => {
        const row = [
          student.studentId,
          student.studentName,
          student.rollNumber,
          ...sortedDates.map(date => {
            const status = student.attendanceByDate.get(date) || '';
            return status === 'Present' ? 'P' : status === 'Absent' ? 'A' : '';
          })
        ];
        tableData.push(row);
      });

      // Add table
      autoTable(doc, {
        head: [headers],
        body: tableData,
        startY: 45,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [41, 128, 185], fontSize: 8 },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          0: { cellWidth: 20 }, // Student ID
          1: { cellWidth: 40 }, // Student Name
          2: { cellWidth: 25 }, // Roll Number
        },
        margin: { top: 45 }
      });

      if (sortedDates.length < allDates.size) {
        const finalY = (doc as any).lastAutoTable.finalY + 10;
        doc.setFontSize(8);
        doc.text(`Note: Showing first ${sortedDates.length} dates of ${allDates.size} total dates. P=Present, A=Absent`, 14, finalY);
      }

      doc.save(`${fileName}.pdf`);
    } catch (error) {
      console.error('Error exporting attendance to PDF:', error);
      throw new Error('Failed to export attendance data to PDF');
    }
  }

  // Export Student Report to Excel
  static exportStudentToExcel(data: StudentAttendanceReport[], fileName: string = 'student_report'): void {
    try {
      const worksheetData = data.map(student => ({
        'Student Name': student.studentName,
        'Roll Number': student.rollNumber,
        'Total Sessions': student.attendanceStats.totalSessions,
        'Present Sessions': student.attendanceStats.presentSessions,
        'Absent Sessions': student.attendanceStats.absentSessions,
        'Attendance Rate': this.formatRate(student.attendanceStats.attendanceRate)
      }));

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      
      worksheet['!cols'] = [
        { wch: 20 }, // Student Name
        { wch: 15 }, // Roll Number
        { wch: 15 }, // Total Sessions
        { wch: 15 }, // Present Sessions
        { wch: 15 }, // Absent Sessions
        { wch: 15 }  // Attendance Rate
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Student Report');
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    } catch (error) {
      console.error('Error exporting student report to Excel:', error);
      throw new Error('Failed to export student report to Excel');
    }
  }

  // Export Student Report to PDF
  static exportStudentToPDF(data: StudentAttendanceReport[], fileName: string = 'student_report'): void {
    try {
      const doc = new jsPDF();
      
      doc.setFontSize(18);
      doc.text('Student Performance Report', 14, 22);
      
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);
      doc.text(`Total Students: ${data.length}`, 14, 36);

      const tableData = data.map(student => [
        student.studentName,
        student.rollNumber,
        student.attendanceStats.totalSessions.toString(),
        student.attendanceStats.presentSessions.toString(),
        student.attendanceStats.absentSessions.toString(),
        this.formatRate(student.attendanceStats.attendanceRate)
      ]);

      autoTable(doc, {
        head: [['Student Name', 'Roll Number', 'Total', 'Present', 'Absent', 'Rate']],
        body: tableData,
        startY: 45,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: 45 }
      });

      doc.save(`${fileName}.pdf`);
    } catch (error) {
      console.error('Error exporting student report to PDF:', error);
      throw new Error('Failed to export student report to PDF');
    }
  }

  // Export Class Report to Excel
  static exportClassToExcel(data: ClassReport[], fileName: string = 'class_report'): void {
    try {
      const worksheetData = data.map(classReport => ({
        'Class Name': classReport.className,
        'Total Students': classReport.totalStudents,
        'Total Sessions': classReport.attendanceStats.totalSessions,
        'Present Sessions': classReport.attendanceStats.presentSessions,
        'Absent Sessions': classReport.attendanceStats.absentSessions,
        'Attendance Rate': this.formatRate(classReport.attendanceStats.attendanceRate)
      }));

      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      
      worksheet['!cols'] = [
        { wch: 20 }, // Class Name
        { wch: 15 }, // Total Students
        { wch: 15 }, // Total Sessions
        { wch: 15 }, // Present Sessions
        { wch: 15 }, // Absent Sessions
        { wch: 15 }  // Attendance Rate
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Class Report');
      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    } catch (error) {
      console.error('Error exporting class report to Excel:', error);
      throw new Error('Failed to export class report to Excel');
    }
  }

  // Export Class Report to PDF
  static exportClassToPDF(data: ClassReport[], fileName: string = 'class_report'): void {
    try {
      const doc = new jsPDF();
      
      doc.setFontSize(18);
      doc.text('Class Performance Report', 14, 22);
      
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);
      doc.text(`Total Classes: ${data.length}`, 14, 36);

      const tableData = data.map(classReport => [
        classReport.className,
        classReport.totalStudents.toString(),
        classReport.attendanceStats.totalSessions.toString(),
        classReport.attendanceStats.presentSessions.toString(),
        classReport.attendanceStats.absentSessions.toString(),
        this.formatRate(classReport.attendanceStats.attendanceRate)
      ]);

      autoTable(doc, {
        head: [['Class Name', 'Students', 'Total', 'Present', 'Absent', 'Rate']],
        body: tableData,
        startY: 45,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { top: 45 }
      });

      doc.save(`${fileName}.pdf`);
    } catch (error) {
      console.error('Error exporting class report to PDF:', error);
      throw new Error('Failed to export class report to PDF');
    }
  }

  // Export Summary Report to Excel
  static exportSummaryToExcel(data: ReportSummary, fileName: string = 'summary_report'): void {
    try {
      const workbook = XLSX.utils.book_new();

      // Overview sheet
      const overviewData = [
        ['Metric', 'Value'],
        ['Total Classes', data.totalClasses],
        ['Total Students', data.totalStudents],
        ['Overall Attendance Rate', this.formatRate(data.overallAttendanceRate)]
      ];
      const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
      overviewSheet['!cols'] = [{ wch: 25 }, { wch: 15 }];

      // Class Reports sheet
      const classData = data.classReports.map(classReport => ({
        'Class Name': classReport.className,
        'Total Students': classReport.totalStudents,
        'Attendance Rate': this.formatRate(classReport.attendanceStats.attendanceRate)
      }));
      const classSheet = XLSX.utils.json_to_sheet(classData);
      classSheet['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }];

      // Top Performing Classes sheet
      const topClassesData = data.topPerformingClasses.map(item => ({
        'Class Name': item.className,
        'Attendance Rate': this.formatRate(item.attendanceRate)
      }));
      const topClassesSheet = XLSX.utils.json_to_sheet(topClassesData);
      topClassesSheet['!cols'] = [{ wch: 20 }, { wch: 15 }];

      // Low Attendance Students sheet
      const lowAttendanceData = data.lowAttendanceStudents.map(item => ({
        'Student Name': item.studentName,
        'Class': item.className,
        'Attendance Rate': this.formatRate(item.attendanceRate)
      }));
      const lowAttendanceSheet = XLSX.utils.json_to_sheet(lowAttendanceData);
      lowAttendanceSheet['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 15 }];

      XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Overview');
      XLSX.utils.book_append_sheet(workbook, classSheet, 'Class Reports');
      XLSX.utils.book_append_sheet(workbook, topClassesSheet, 'Top Classes');
      XLSX.utils.book_append_sheet(workbook, lowAttendanceSheet, 'Low Attendance');

      XLSX.writeFile(workbook, `${fileName}.xlsx`);
    } catch (error) {
      console.error('Error exporting summary report to Excel:', error);
      throw new Error('Failed to export summary report to Excel');
    }
  }

  // Export Summary Report to PDF
  static exportSummaryToPDF(data: ReportSummary, fileName: string = 'summary_report'): void {
    try {
      const doc = new jsPDF();
      
      doc.setFontSize(18);
      doc.text('Summary Report', 14, 22);
      
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

      // Overview section
      doc.setFontSize(14);
      doc.text('Overview', 14, 45);
      
      doc.setFontSize(10);
      doc.text(`Total Classes: ${data.totalClasses}`, 14, 55);
      doc.text(`Total Students: ${data.totalStudents}`, 14, 62);
      doc.text(`Overall Attendance Rate: ${this.formatRate(data.overallAttendanceRate)}`, 14, 69);

      // Class performance table
      doc.setFontSize(14);
      doc.text('Class Performance', 14, 85);

      const classTableData = data.classReports.map(classReport => [
        classReport.className,
        classReport.totalStudents.toString(),
        this.formatRate(classReport.attendanceStats.attendanceRate)
      ]);

      autoTable(doc, {
        head: [['Class Name', 'Students', 'Attendance Rate']],
        body: classTableData,
        startY: 92,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [41, 128, 185] },
        alternateRowStyles: { fillColor: [245, 245, 245] }
      });

      // Top performing classes (if there's space)
      if (data.topPerformingClasses.length > 0) {
        const finalY = (doc as any).lastAutoTable.finalY || 150;
        
        doc.setFontSize(14);
        doc.text('Top Performing Classes', 14, finalY + 15);

        const topClassesData = data.topPerformingClasses.slice(0, 5).map(item => [
          item.className,
          this.formatRate(item.attendanceRate)
        ]);

        autoTable(doc, {
          head: [['Class Name', 'Attendance Rate']],
          body: topClassesData,
          startY: finalY + 22,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [46, 125, 50] },
          alternateRowStyles: { fillColor: [245, 245, 245] }
        });
      }

      doc.save(`${fileName}.pdf`);
    } catch (error) {
      console.error('Error exporting summary report to PDF:', error);
      throw new Error('Failed to export summary report to PDF');
    }
  }

  // Main export function that determines the format and data type
  static exportReport(
    reportType: string,
    format: 'excel' | 'pdf',
    data: any,
    customFileName?: string
  ): void {
    try {
      const timestamp = new Date().toISOString().slice(0, 10);
      const baseFileName = customFileName || `${reportType}_report_${timestamp}`;

      switch (reportType) {
        case 'attendance':
          if (format === 'excel') {
            this.exportAttendanceToExcel(data, baseFileName);
          } else {
            this.exportAttendanceToPDF(data, baseFileName);
          }
          break;

        case 'student':
          if (format === 'excel') {
            this.exportStudentToExcel(data, baseFileName);
          } else {
            this.exportStudentToPDF(data, baseFileName);
          }
          break;

        case 'class':
          if (format === 'excel') {
            this.exportClassToExcel(data, baseFileName);
          } else {
            this.exportClassToPDF(data, baseFileName);
          }
          break;

        case 'summary':
          if (format === 'excel') {
            this.exportSummaryToExcel(data, baseFileName);
          } else {
            this.exportSummaryToPDF(data, baseFileName);
          }
          break;

        default:
          throw new Error(`Unsupported report type: ${reportType}`);
      }
    } catch (error) {
      console.error('Error in exportReport:', error);
      throw error;
    }
  }
} 